import { postPrivateAssumeMutation } from '@onefootprint/fetch-dashboard';
import { ThemedLogoFpMark } from '@onefootprint/icons';
import { useShortcut, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DASHBOARD_IS_LIVE_HEADER } from '@/config/constants';
import useOrgSession from '@/hooks/use-org-session';
import useSession from '@/hooks/use-session';
import useUserSession from '@/hooks/use-user-session';
export type OrgSwitcherProps = {
  children: React.ReactNode;
};
const OrgSwitcher = ({
  children
}: OrgSwitcherProps) => {
  const {
    t
  } = useTranslation('common', {
    keyPrefix: 'components.privateLayout.orgSwitcher'
  });
  const org = useOrgSession();
  const user = useUserSession();
  const {
    logIn,
    data: {
      auth
    }
  } = useSession();
  const assumeRoleMutation = useMutation(postPrivateAssumeMutation({
    headers: {
      [DASHBOARD_IS_LIVE_HEADER]: null
    }
  }));
  const isFirmEmployee = user.data?.isFirmEmployee;
  // Init from the URL synchronously so children don't mount and auto-assume a competing tenant
  // before the assume effect runs.
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('org_id') || params.has('mode');
  });
  const {
    toast
  } = useToast();
  const shareUrl = () => {
    const url = new URL(window.location.href);
    const mode = org.sandbox.isSandbox ? 'sandbox' : 'live';
    const orgId = org.data?.id;
    if (orgId) {
      url.searchParams.set('org_id', orgId);
    }
    if (mode) {
      url.searchParams.set('mode', mode);
    }
    navigator.clipboard.writeText(url.toString());
    toast({
      title: t($ => $.shareUrl.title),
      description: t($_0 => $_0.shareUrl.description)
    });
  };
  useShortcut({
    keys: ['ctrl+s'],
    onShortcut: shareUrl
  });
  const copyAuthToken = () => {
    navigator.clipboard.writeText(auth || '');
    toast({
      title: 'Success!',
      description: 'Copied auth token to clipboard'
    });
  };
  useShortcut({
    keys: ['ctrl+b'],
    onShortcut: copyAuthToken,
    disabled: !isFirmEmployee
  });
  const shouldAssumeOrg = (orgId_0?: string) => {
    if (!orgId_0) {
      return false;
    }
    return orgId_0 !== org.data?.id && isFirmEmployee;
  };
  const shouldChangeMode = (mode_0?: string) => {
    if (!mode_0 || mode_0 !== 'sandbox' && mode_0 !== 'live') {
      return false;
    }
    const currentMode = org.sandbox.isSandbox ? 'sandbox' : 'live';
    return currentMode !== mode_0;
  };
  const removeOrgIdAndModeFromUrl = () => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('org_id');
    newUrl.searchParams.delete('mode');
    window.history.replaceState({}, '', newUrl.toString());
  };
  const assume = async ({
    mode: mode_1,
    orgId: orgId_1
  }: {
    mode?: string;
    orgId?: string;
  }) => {
    try {
      if (shouldAssumeOrg(orgId_1) && orgId_1) {
        const {
          token
        } = await assumeRoleMutation.mutateAsync({
          body: {
            tenantId: orgId_1
          }
        });
        // A URL with an org_id but no mode shouldn't change the live/sandbox setting.
        const hasMode = mode_1 === 'live' || mode_1 === 'sandbox';
        await logIn({
          auth: token,
          newIsLive: hasMode ? mode_1 === 'live' : undefined
        });
      } else if (shouldChangeMode(mode_1)) {
        await org.sandbox.toggle();
      }
    } catch (error) {
      console.error(error);
    } finally {
      removeOrgIdAndModeFromUrl();
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode_2 = urlParams.get('mode');
    const orgId_2 = urlParams.get('org_id');
    if (mode_2 || orgId_2) {
      setIsLoading(true);
      assume({
        mode: mode_2 ?? undefined,
        orgId: orgId_2 ?? undefined
      });
    }
  }, []);
  return isLoading ? <div className="flex items-center justify-center h-screen">
      <motion.div className="flex items-center justify-center w-[260px] h-[260px]" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 0]
    }} transition={{
      duration: 4,
      repeat: Number.POSITIVE_INFINITY,
      repeatType: 'loop'
    }}>
        <ThemedLogoFpMark color="primary" className="size-10" />
      </motion.div>
    </div> : children;
};
export default OrgSwitcher;